import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { KEYBOARD_KEYS, KEYBOARD_EVENTS } from '@CommonConstants';
import { CHOICE_RESULT_KEYS, ITEM_TEXT_DELIMITER_TYPE } from '@Services/Editor/Constants';

import useOffsetSize from '@Hooks/useOffsetSize';
import useKeyboardNavigation from '@Hooks/useKeyboardNavigation';
import { useItemNamesByType } from '@Services/Editor';

import TX from '@Components/Common/Text';
import { ContextMenu, Item } from '@Components/Common/ContextMenu';

const DEFAULT_ITEM_LENGTH = 1;

const AutoComplete = ({
  targetRef,
  isVisibleDefaultAutoComplete,
  isContextMenuVisible,
  keyword,
  handleAutoCompleteItemClick,
}) => {
  const [selectorType, setSelectorType] = useState(CHOICE_RESULT_KEYS.STORE);
  const [autoCompleteList, setAutoCompleteList] = useState([]);

  const isVisibleDefaultAutoCompleteItem = !!(
    isVisibleDefaultAutoComplete &&
    keyword.length &&
    (!autoCompleteList.length || !autoCompleteList.find(item => item === keyword))
  );
  const isVisibleAutoComplete = !!(autoCompleteList.length || keyword.length);
  const autoCompleteListLength = autoCompleteList.length + (isVisibleDefaultAutoCompleteItem ? DEFAULT_ITEM_LENGTH : 0);

  const itemElementsByType = useSelector(useItemNamesByType(selectorType), shallowEqual);
  const { left, top } = useOffsetSize(targetRef, isContextMenuVisible);
  const { isKeyPressed: isEnterKeyPressed, selectedIndex } = useKeyboardNavigation(
    autoCompleteListLength,
    KEYBOARD_KEYS.ENTER,
    KEYBOARD_EVENTS.KEY_UP,
  );

  const setTypeByHiddenItemDelimiter = () => {
    if (!keyword.startsWith(ITEM_TEXT_DELIMITER_TYPE.HIDDEN_ITEM)) {
      setSelectorType(CHOICE_RESULT_KEYS.STORE);
    } else {
      setSelectorType(CHOICE_RESULT_KEYS.STORE_HIDDEN);
    }
  };

  const setAutoCompleteListByKeyword = () => {
    if (!keyword) {
      return;
    }

    const replacedKeyWord =
      selectorType === CHOICE_RESULT_KEYS.STORE_HIDDEN
        ? keyword.replace(`${ITEM_TEXT_DELIMITER_TYPE.HIDDEN_ITEM}`, '')
        : keyword;
    const filteredSuggestionsList = itemElementsByType.filter(
      item => item.startsWith(replacedKeyWord) || item.includes(replacedKeyWord),
    );

    setAutoCompleteList(filteredSuggestionsList);
  };

  useEffect(() => {
    setTypeByHiddenItemDelimiter();
    setAutoCompleteListByKeyword();
  }, [keyword]);

  useEffect(() => {
    const targetItemName = autoCompleteList[selectedIndex] || keyword;

    if (isEnterKeyPressed) {
      handleAutoCompleteItemClick(targetItemName);
    }
  }, [isEnterKeyPressed]);

  if (!isVisibleAutoComplete) {
    return null;
  }

  return (
    <ContextMenu position={{ left, top: top + 36 }}>
      {autoCompleteList.length ? (
        <ItemContainer>
          {autoCompleteList.map((item, index) => {
            const isSelected = index === selectedIndex;

            return (
              <Item key={item} selected={isSelected} onClick={() => handleAutoCompleteItemClick(item)}>
                <TX.FootnoteM>{item}</TX.FootnoteM>
              </Item>
            );
          })}
        </ItemContainer>
      ) : null}

      {isVisibleDefaultAutoCompleteItem ? (
        <Item
          selected={autoCompleteListLength - DEFAULT_ITEM_LENGTH === selectedIndex}
          onClick={() => handleAutoCompleteItemClick(keyword)}
        >
          <TX.FootnoteM>{`+ "${keyword}" 아이템 추가`}</TX.FootnoteM>
        </Item>
      ) : null}
    </ContextMenu>
  );
};

AutoComplete.propTypes = {
  targetRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),
  }),
  isVisibleDefaultAutoComplete: PropTypes.bool,
  isContextMenuVisible: PropTypes.bool.isRequired,
  keyword: PropTypes.string,
  handleAutoCompleteItemClick: PropTypes.func.isRequired,
};

AutoComplete.defaultProps = {
  targetRef: undefined,
  isVisibleDefaultAutoComplete: true,
  keyword: '',
};

const ItemContainer = styled.div``;

export default AutoComplete;
